import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaImage, FaDownload, FaCog, FaChevronDown, FaSync, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function AIImageGen() {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('@cf/stabilityai/stable-diffusion-xl-base-1.0');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageVersions, setImageVersions] = useState([]); // Store multiple versions of generated images
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [lastUsedPrompt, setLastUsedPrompt] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount
  }, []);

  const fetchModels = async () => {
    try {
      const response = await fetch('/api/manage/aiChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'getAvailableModels', 
          userId, 
          token, 
          provider: 'cloudflare',
          task: 'Text-to-Image'
        })
      });
      const data = await response.json();
      if (data.success) {
        setModels(data.models);
        if (data.models.length > 0 && !selectedModel) {
          setSelectedModel(data.models[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching models:', err);
    }
  };

  const generateImage = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const promptToUse = prompt.trim();
    setLastUsedPrompt(promptToUse);

    try {
      const response = await fetch('/api/manage/aiChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generateImage',
          userId,
          token,
          prompt: promptToUse,
          model: selectedModel
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setGeneratedImage(data.image);
        // Reset versions and add the first image
        setImageVersions([data.image]);
        setCurrentVersionIndex(0);
        setSuccessMessage('Image generated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to generate image');
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setError('Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const regenerateImage = async () => {
    if (!lastUsedPrompt) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/manage/aiChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generateImage',
          userId,
          token,
          prompt: lastUsedPrompt,
          model: selectedModel
        })
      });
      const data = await response.json();
      
      if (data.success) {
        // Add new image to versions
        const newVersions = [...imageVersions, data.image];
        setImageVersions(newVersions);
        setCurrentVersionIndex(newVersions.length - 1);
        setGeneratedImage(data.image);
        setSuccessMessage('Image regenerated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to regenerate image');
      }
    } catch (err) {
      console.error('Error regenerating image:', err);
      setError('Failed to regenerate image');
    } finally {
      setLoading(false);
    }
  };

  const switchImageVersion = (direction) => {
    if (imageVersions.length <= 1) return;
    
    let newIdx = currentVersionIndex + direction;
    
    // Wrap around
    if (newIdx < 0) newIdx = imageVersions.length - 1;
    if (newIdx >= imageVersions.length) newIdx = 0;
    
    setCurrentVersionIndex(newIdx);
    setGeneratedImage(imageVersions[newIdx]);
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadImage = async () => {
    if (!generatedImage) return;

    const filename = `ai-generated-${Date.now()}.png`;

    try {
      // For CDN URLs, we need to fetch and create a blob to avoid CORS issues
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      triggerDownload(url, filename);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading image:', err);
      // Fallback to direct link
      triggerDownload(generatedImage, filename);
    }
  };

  return (
    <ImageGenContainer>
      <Header>
        <HeaderContent>
          <Title>
            <FaImage /> AI Image Generation
          </Title>
          <ModelSelector>
            <FaCog />
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {models.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
            <FaChevronDown />
          </ModelSelector>
        </HeaderContent>
      </Header>

      <ContentArea>
        <PromptSection>
          <PromptForm onSubmit={generateImage}>
            <PromptInput
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              disabled={loading}
            />
            <GenerateButton type="submit" disabled={loading || !prompt.trim()}>
              {loading ? 'Generating...' : 'Generate Image'}
            </GenerateButton>
          </PromptForm>
        </PromptSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

        {loading && (
          <LoadingState>
            <LoadingSpinner />
            <p>Generating your image...</p>
          </LoadingState>
        )}

        {generatedImage && !loading && (
          <ImageDisplay>
            <GeneratedImage src={generatedImage} alt="Generated AI Image" />
            <ImageActions>
              {imageVersions.length > 1 && (
                <VersionNavigation>
                  <VersionButton
                    onClick={() => switchImageVersion(-1)}
                    disabled={loading}
                    title="Previous version"
                  >
                    <FaChevronLeft />
                  </VersionButton>
                  <VersionIndicator>
                    {currentVersionIndex + 1} / {imageVersions.length}
                  </VersionIndicator>
                  <VersionButton
                    onClick={() => switchImageVersion(1)}
                    disabled={loading}
                    title="Next version"
                  >
                    <FaChevronRight />
                  </VersionButton>
                </VersionNavigation>
              )}
              <RegenerateButton onClick={regenerateImage} disabled={loading}>
                <FaSync /> Regenerate
              </RegenerateButton>
              <DownloadButton onClick={downloadImage}>
                <FaDownload /> Download Image
              </DownloadButton>
            </ImageActions>
            {lastUsedPrompt && (
              <PromptDisplay>
                <strong>Prompt:</strong> {lastUsedPrompt}
              </PromptDisplay>
            )}
          </ImageDisplay>
        )}

        {!generatedImage && !loading && (
          <EmptyState>
            <FaImage size={64} />
            <h3>Generate AI Images</h3>
            <p>Enter a prompt above to generate an image using AI</p>
          </EmptyState>
        )}
      </ContentArea>
    </ImageGenContainer>
  );
}

// Styled Components
const ImageGenContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--card-bg);
`;

const Header = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--card-border);
  background-color: var(--bg-secondary);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModelSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;

  select {
    background-color: var(--bg-primary);
    color: var(--text-primary);
    border: 1px solid var(--card-border);
    border-radius: var(--border-radius);
    padding: 0.5rem;
    cursor: pointer;
    min-width: 200px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PromptSection = styled.div`
  width: 100%;
`;

const PromptForm = styled.form`
  display: flex;
  gap: 1rem;
  width: 100%;
`;

const PromptInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const GenerateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  border-radius: var(--border-radius);
  border: 1px solid rgba(255, 0, 0, 0.3);
`;

const SuccessMessage = styled.div`
  padding: 0.75rem;
  background-color: rgba(76, 175, 80, 0.1);
  color: #4caf50;
  border-radius: var(--border-radius);
  border: 1px solid rgba(76, 175, 80, 0.3);
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  color: var(--text-secondary);
`;

const LoadingSpinner = styled.div`
  border: 4px solid var(--card-border);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ImageDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const GeneratedImage = styled.img`
  max-width: 100%;
  max-height: 600px;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ImageActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const VersionNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--bg-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  padding: 0.5rem;
`;

const VersionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover:not(:disabled) {
    color: var(--accent-color);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    font-size: 0.9rem;
  }
`;

const VersionIndicator = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
  padding: 0 0.5rem;
  min-width: 50px;
  text-align: center;
  font-weight: 500;
`;

const RegenerateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--accent-color);
    color: white;
    border-color: var(--accent-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DownloadButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const PromptDisplay = styled.div`
  padding: 1rem;
  background-color: var(--bg-secondary);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  max-width: 800px;
  text-align: center;

  strong {
    color: var(--text-secondary);
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  gap: 1rem;
  padding: 3rem;

  h3 {
    color: var(--text-primary);
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

export default AIImageGen;
